import { LitElement, html, css, PropertyValues } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import './vessel-performance-full-voyage-execution'; 
import './vessel-performance-full-voyage-planning'; 
import './vessel-performance-current-leg';
import './vessel-performance-legbyleg';

import { VesselPlanningModel } from '../../../src/models/vessel-planning-model';
import { VesselPerformanceModel, CurrentLeg } from '../../../src/models/vessel-performance-model';
import { PerformanceUtils } from '../../../src/utils/performance-utils';
import { VoyageRoutingModel } from '../../../src/models/voyage-routing-model';
import { VoyagePlan } from '../../../src/models/vessel-planning-model';
import { VoyageDialogPopupManager } from '../../voyage-dialog-popup/src/voyage-dialog-popup-manager';
import { SaveHydraLog } from '../../../src/utils/route-utils';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { TimeseriesData } from '../../voy-timeseries-chart/src/echartUtils';
import { SuggestedRouteEvents } from '../../voyage-suggested-routes/src/types';

export interface WeatherData {
    wind50M: TimeseriesData[],
    swell: TimeseriesData[],
    currents: TimeseriesData[],
    wave: TimeseriesData[]
}

@customElement('vessel-performance-main')
export class VesselPerformanceMain extends LitElement {
    
    @property({type: String}) mode: string = "";
    @property({type: Boolean}) shown: boolean = false;
    @property({type: Number}) playerTime: number = -1;
    @property({type: Object}) plan: VoyagePlan|null = null;

    @property({attribute: false}) private _maximized: boolean = false;

    @property({attribute: false}) private graphType: string = "current_leg";
    @property({attribute: false}) private _currentLegData: CurrentLeg|null = null;
    private _companyMaxRollAngleLimit: number | null = null;

    constructor() {
        super();
        this._graphTypeChanged = this._graphTypeChanged.bind(this);
        this._updateCurrentLegData = this._updateCurrentLegData.bind(this);
        this._updatePerformanceOnRouteChanged = this._updatePerformanceOnRouteChanged.bind(this);
        
        this.playerTime = Date.now();
    }
    
    static get styles() {
        return [
            css`
            :host {
                display: none;
                pointer-events: none;
            }

            :host([shown]) {
                display: flex;
                position: fixed;
                top: 110px;
                right: 15px;
                z-index: 3;
            }

            .graph-type {
                
                width: 252px;
                min-height: 41px;
                right: 0;
                background: var(--neutrals-grey-10);
                color: var(--neutrals-grey-03, black);
                box-shadow: 1px 1px 5px var(--secondary-voyage-box-shadow);
                border: 1px solid var(--border-color-1, black);
                display: flex;
                align-items: center;
                justify-content: space-evenly;
                pointer-events: auto;
                margin-left: auto;
            }

            .main {
                position: absolute;
                top: 51px;
                right: 0px;
            }

            .container {
                font-family: 'MaerskText';
                font-size: 14px;
            }

            .chart-container {
                width: 390px;
                height: 100%;
                height: 640px;
            }

            .chart-container[maximized] {
                width: calc(100vw - 452px);
                height: calc(100vh - 220px);
            }

            .header {
                display: grid;
                grid-template-columns: auto auto;
                grid-gap: 10px;
            }

            .report-problem {
                background: var(--neutrals-grey-10);
                color: var(--performance-viewer-simulated-color);
                pointer-events: auto;
                box-shadow: 1px 1px 5px var(--secondary-voyage-box-shadow);
                border: 1px solid var(--border-color-1, black);
                cursor: pointer;
                height: 100%;
                user-select: none;
                outline: none;
            }

            .report-problem:hover {
                background: var(--neutrals-grey-08);
            }

            .expand-container {
                display: flex;
                justify-content: center;
                align-items: center;
                position: absolute;
                margin-left: calc(100% - 55px);
                margin-top: 15px;
                height: 20px;
                width: 20px;
                background-color: white;
                z-index: 10;
                cursor: pointer;
                pointer-events: auto;
            }

            .expand-container > svg {
                fill: var(--neutrals-grey-02, black);
                height: 90%;
                width: 90%;
            }
        `];
    }

    render() {
        switch(this.mode) {
            case "schedule":
                return this._renderForSchedule();
            case "scheduleEditor": 
                return this._renderForSchedule();
            case "viewRoute":
                return this._renderForSchedule();
            case "suggestedRoutes":
                return this._renderForSuggestedRoutes();
            default:
                return html``;
        }
    }

    private _renderForSchedule() {
        return html`
                <div class="container">
                    <div class="header">
                        <button class="report-problem" id="report-problem-button" @click=${this._reportProblem}>Report problem</button>
                        <div class="graph-type">
                            <label for="currentLeg">
                                <input id="currentLeg" name="graphType" type="radio" value="current_leg" @change=${this._graphTypeChanged}/>
                                    Current leg
                            </label>
                            <label for="fullVoyage"> 
                                <input id="fullVoyage" name="graphType" type="radio" value="full_voyage" @change=${this._graphTypeChanged}/>
                                    Full voyage
                            </label>
                        </div>
                    </div>
                    
                    <div class="main">
                        ${this.graphType === 'current_leg'
                        ? html`
                        <vessel-performance-current-leg .data=${this._currentLegData}></vessel-performance-current-leg>` 
                        : html`
                        <div class="chart-container" ?maximized=${this._maximized}>
                            <div class="expand-container" title="${this._maximized ? 'Minimize performance charts' : 'Maximize performance charts'}" @click=${this._setMaximized}>
                                ${this._maximized ? SvgIcons.solidCompressArrowsAlt : SvgIcons.solidExpandArrowsAlt}
                            </div>
                            <vessel-performance-full-voyage-execution ._rollAngleThresholdLimit=${this._companyMaxRollAngleLimit} .maximized=${this._maximized}></vessel-performance-full-voyage-execution>
                        </div>
                        `}
                    </div>
                </div>
                `;
    }

    private async _reportProblem() {
        const dialog = VoyageDialogPopupManager.createDialog({
            headerText: "Report Problem With Performance",
            bodyText: `If you believe there is something wrong with the Performance\n calculations, click "Download log" to generate log file and send it\n attached in an e-mail support ticket. Support will try to fix it as soon as possible.`,
            acceptButtonText: "Download log",
            rejectButtonText: "Cancel"
        });

        const response = await dialog.getResponse();
        if (response === "accept")
        {
            const hydraRequestId = this.plan!.routes[this.plan!.selectedRouteIndex].route_info!.hydraRequestId;
            if (!hydraRequestId || hydraRequestId === "") {
                return;
            }

            SaveHydraLog(hydraRequestId);
        }
    }

    private _renderForSuggestedRoutes() {
        return html`
                <div class="container">
                    <div class="graph-type">
                        <label for="currentLeg">
                            <input id="currentLeg" name="graphType" type="radio" value="current_leg" @change=${this._graphTypeChanged}/>
                                Leg-by-leg
                        </label>
                        <label for="fullVoyage"> 
                            <input id="fullVoyage" name="graphType" type="radio" value="full_voyage" @change=${this._graphTypeChanged}/>
                                Full voyage
                        </label>
                    </div>

                    <div class="main">
                        ${this.graphType === 'current_leg' 
                        ? html`
                        <vessel-performance-current-leg .data=${this._currentLegData}></vessel-performance-current-leg>` 
                        : html`
                        <div class="chart-container" ?maximized=${this._maximized}>
                            <div class="expand-container" title="${this._maximized ? 'Minimize performance charts' : 'Maximize performance charts'}" @click=${this._setMaximized}>
                                ${this._maximized ? SvgIcons.solidCompressArrowsAlt : SvgIcons.solidExpandArrowsAlt}
                            </div>
                            <vessel-performance-full-voyage-planning ._rollAngleThresholdLimit=${this._companyMaxRollAngleLimit} .currentTime=${this.playerTime} .plan=${this.plan} .maximized=${this._maximized}></vessel-performance-full-voyage-planning>
                        </div>`}
                    </div>
                </div>
                `;
    }

    private _graphTypeChanged(evt: { target: { value: string; }; }) {
        this.graphType = evt.target.value;
    }

    firstUpdated(properties: PropertyValues) {
        //set initial value on current leg
        const currentLeg = <HTMLInputElement>this.shadowRoot!.getElementById("currentLeg");
        if (currentLeg) {
            currentLeg.checked = true;
        }

        VoyageRoutingModel!.addEventListener(SuggestedRouteEvents.RoutesChanged, this._updatePerformanceOnRouteChanged);
        VesselPerformanceModel!.addEventListener('executing-performance-data-updated', this._updateCurrentLegData);
        
        this._updatePerformanceOnRouteChanged();
        this._updateCurrentLegData();
    }

    updated(changedProperties: PropertyValues) {
        changedProperties.forEach((oldValue, propName) => {
            if (propName === "plan" && this.plan?.rollAngle) {
                this._companyMaxRollAngleLimit = this.plan?.rollAngle;
            }

            if (propName === "plan" || propName === "mode" || propName === "shown") {
                if (this.graphType === 'current_leg') {
                    const currentLeg = <HTMLInputElement>this.shadowRoot!.getElementById("currentLeg");
                    if (currentLeg) {
                        currentLeg.checked = true;
                    }
                }
                if (this.graphType === 'full_voyage') {
                    const fullVoyage = <HTMLInputElement>this.shadowRoot!.getElementById("fullVoyage");
                    if (fullVoyage) {
                        fullVoyage.checked = true;
                    }
                }
                this._updateCurrentLegData();
                this.requestUpdate();
            }
        });
    }

    private _updatePerformanceOnRouteChanged() {
        VesselPerformanceModel!.syncUiRoutesData();
    }

    private async _updateCurrentLegData() {
        this._currentLegData = await VesselPerformanceModel!.getCurrentLegData();
        this.requestUpdate();
    }

    private _setMaximized() {
        this._maximized = !this._maximized;
    }
};
